import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { useFormBuilderStore } from '@/stores/formBuilderStore'
import type { Question } from '@/types/form'
import { CopyPlus, LockKeyhole, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

type CopyQuestionToGroupDialogProps = {
  question: Question | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CopyQuestionToGroupDialog({
  question,
  open,
  onOpenChange,
}: CopyQuestionToGroupDialogProps) {
  const questionGroups = useFormBuilderStore((state) => state.questionGroups)
  const copyQuestionToGroupInstance = useFormBuilderStore(
    (state) => state.copyQuestionToGroupInstance,
  )
  const [keyword, setKeyword] = useState('')
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
  const filteredGroups = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase()
    if (!normalizedKeyword) return questionGroups
    return questionGroups.filter((group) =>
      [group.title, group.description, group.mode]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase().includes(normalizedKeyword)),
    )
  }, [keyword, questionGroups])
  const selectedGroup = questionGroups.find((group) => group.id === selectedGroupId)
  const canCopy = Boolean(question && selectedGroup?.mode === 'copy')

  function copyQuestion() {
    if (!question || !selectedGroupId) return
    const result = copyQuestionToGroupInstance(question.id, selectedGroupId)
    if (result.ok) {
      toast.success(result.message)
      onOpenChange(false)
      setSelectedGroupId(null)
      setKeyword('')
      return
    }
    toast.error(result.message)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(680px,calc(100dvh-48px))] max-w-2xl flex-col overflow-hidden p-0">
        <div className="shrink-0 border-b border-slate-200 p-5">
          <DialogHeader>
            <DialogTitle>建立複本至題組</DialogTitle>
            <DialogDescription>
              將「{question?.title ?? '目前問題'}」建立一份複本到指定題組。清單會顯示所有題組；同步題組維持唯讀。
            </DialogDescription>
          </DialogHeader>
        </div>
        <div className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)_auto]">
          <div className="border-b border-slate-200 p-5">
            <div className="relative">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜尋問卷內題組"
                className="pl-9"
              />
            </div>
          </div>
          <ScrollArea className="min-h-0" viewportClassName="p-5">
            {filteredGroups.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                目前問卷還沒有題組。請先加入同步題組或建立複本題組。
              </div>
            ) : (
              <div className="space-y-3">
                {filteredGroups.map((group) => {
                  const readonly = group.mode === 'synced'
                  return (
                    <button
                      key={group.id}
                      type="button"
                      disabled={readonly}
                      className={cn(
                        'w-full rounded-lg border p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-70',
                        selectedGroupId === group.id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 bg-white hover:border-indigo-200',
                      )}
                      onClick={() => setSelectedGroupId(group.id)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-slate-950">{group.title}</div>
                          <p className="mt-1 text-sm leading-6 text-slate-500">
                            {group.description || '尚未填寫題組描述'}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {readonly && <LockKeyhole className="size-4 text-amber-600" />}
                          <Badge variant={readonly ? 'warning' : 'secondary'}>
                            {readonly ? '同步唯讀' : '複本題組'}
                          </Badge>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-slate-500">
                        {group.questionIds.length} 題
                        {readonly ? ' · 需回到題組管理修改' : ' · 可加入問題複本'}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </ScrollArea>
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-200 p-5">
            <div className="flex min-w-0 items-center gap-2 text-sm text-slate-500">
              <CopyPlus className="size-4 shrink-0" />
              <span className="truncate">
                {selectedGroup ? `目標題組：${selectedGroup.title}` : '請選擇複本題組'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button type="button" disabled={!canCopy} onClick={copyQuestion}>
                建立複本
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
